import "@root/style.css";
import React from "react";
import ReactDOM from "react-dom/client";
import App from "@root/App";
import { BrowserRouter as Router } from "react-router-dom";
import { ConfigCatProvider } from "configcat-react";
import { sunboundPayProxy } from "@proxies";
import { extractTransferToken, redeemTransferToken, extractOAuthResult, applyOAuthResult, redirectWithAuthTransfer, takeOAuthLoginResult } from "@stores";
import { SpinnerProvider, SessionProvider, SettingsProvider, ConfigCatUserProvider } from "@common";
import { helpers } from "@shared";
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";
import { PosthogIdentifyProvider, capturePosthogEvent, PostHogEvents } from "@analytics";
import { trackMouseUsage } from "./setup/accessibility";

declare global {
    interface Window {
        settings: {
            ENV: string;
            SUNBOUNDPAY_API: string;
            SUNBOUNDPAY_PAYABLI_PUBLIC_TOKEN: string;
            CONFIGCAT_SDK_KEY: string;
            POSTHOG_API_KEY?: string;
            POSTHOG_HOST?: string;
            POSTHOG_SITE_ID?: string;
        };
    }
}

// Strip any transfer token / OAuth redirect result from the URL immediately, before
// anything else (analytics, Sentry) runs and can capture the params.
const transferToken = extractTransferToken();
const oauthResult = extractOAuthResult();

// Must be initialized before the async IIFE below, which may call the proxy
// to redeem the transfer token.
sunboundPayProxy.init(window.settings.SUNBOUNDPAY_API);

trackMouseUsage();

Sentry.init({
    dsn: `https://5c37fb4f0561a28f1fabca14e8fe8740@o4508608607879168.ingest.us.sentry.io/4508614478856192`,
    environment: window.settings.ENV
});

// Only send analytics for prod.
// To test locally, remove && window.settings.ENV === `prod`
if (window.settings.POSTHOG_API_KEY && window.settings.ENV === `prod`) {
    const analyticsContext = {
        environment: window.settings.ENV,
        site: window.settings.POSTHOG_SITE_ID
    };
    /* eslint-disable camelcase */
    posthog.init(window.settings.POSTHOG_API_KEY, {
        api_host: window.settings.POSTHOG_HOST ?? `https://us.i.posthog.com`,
        person_profiles: `always`,
        capture_pageview: true,
        capture_pageleave: true,
        before_send: event => {
            if (!event) return event;
            event.properties = {
                ...analyticsContext,
                ...(event.properties ?? {})
            };
            return event;
        }
    });
    /* eslint-enable camelcase */
    posthog.register(analyticsContext);
}

function renderApp() {
    const Navigation = () => (
        <SettingsProvider>
            <SessionProvider>
                <PosthogIdentifyProvider>
                    <ConfigCatProvider sdkKey={window.settings.CONFIGCAT_SDK_KEY}>
                        <ConfigCatUserProvider>
                            <SpinnerProvider>
                                <Router>
                                    <App />
                                </Router>
                            </SpinnerProvider>
                        </ConfigCatUserProvider>
                    </ConfigCatProvider>
                </PosthogIdentifyProvider>
            </SessionProvider>
        </SettingsProvider>
    );

    const container = document.getElementById(`root`);

    if (container !== null) {
        const root = ReactDOM.createRoot(container);

        root.render(
            <React.StrictMode>
                <Navigation />
            </React.StrictMode>
        );
    } else {
        console.error(`Failed to find the root element`);
    }
}

(async () => {
    if (transferToken) await redeemTransferToken(transferToken);
    if (oauthResult) {
        const appliedOAuthResult = applyOAuthResult(oauthResult);
        if (appliedOAuthResult.status === `success`) {
            capturePosthogEvent(PostHogEvents.USER_LOGGED_IN, { loginMethod: appliedOAuthResult.provider });
            if (oauthResult.communityShortName) {
                const targetHost = helpers.getCommunitySubdomain(oauthResult.communityShortName);
                if (window.location.host !== targetHost) {
                    const oauthLoginResult = takeOAuthLoginResult();
                    const targetPath = oauthLoginResult?.status === `success` ? oauthLoginResult.redirectPath ?? `/` : `/`;
                    await redirectWithAuthTransfer(targetHost, targetPath);
                    return;
                }
            }
        }
    }
    renderApp();
})();
